import { IRowData } from "@multiweb/types/contracts";
import { DateUtils } from "../../../Global/Base/Utils/DateUtils";
import { SplitPanel } from "../../UI.ElementarControls/SplitPanel";
import { createElement, registerCustomElement } from "../../UI.ElementarControls/Utils/CustomElementUtils";
import { CalendarGrid } from "../CalendarGrid";
import { CalendarCell } from "./CalendarCell";
import { CalendarEvent } from "./CalendarEvent";
import { CalendarEventItem, CalendarEventItemType } from "./CalendarEventItem";
import { CalendarHourEvent } from "./CalendarHourEvent";
import { DayHourCell } from "./DayHourCell";

export class DayCalendarGrid extends CalendarGrid {
    /**
     * Buňky celodenních událostí
     */
    protected _allDayCells: Map<string, DayHourCell> = new Map<string, DayHourCell>();
    /**
     * Buňky ostatních událostí
     */
    protected override _dayCells: Map<string, DayHourCell> = new Map<string, DayHourCell>();
    private _splitPanel: SplitPanel;

    protected _firstHour = 8;

    private _detailDiv: HTMLElement;
    private _detailBody: HTMLElement;
    private _detailTitleDiv: HTMLElement;
    private _detailTimeDiv: HTMLElement;
    private _detailTextDiv: HTMLElement;
    private _actionsMenuButton: HTMLElement;
    protected _calendarEvents: Array<CalendarHourEvent>;

    public override set currentDate(v: Date) {
        if (v == undefined)
            return;

        this._currentDate = v;

        this._redraw();
    }

    public override get currentDate(): Date {
        return this._currentDate;
    }

    public get dateSpan(): Date {
        const lastDay = new Date(this._currentDate);
        lastDay.setDate(lastDay.getDate() + 1);
        lastDay.setHours(0, 0, 0, 0);

        return lastDay;
    }

    protected override _constructor(): void {
        super._constructor();
        this.classList.add("day");
    }

    public override selectedCellTimeSpan(): { start: Date; end: Date } {
        const start = this.selectedCell.idOfCell;
        const end = new Date(start);
        end.setHours(end.getHours(), 59, 59, 59);
        return { start: start, end: end };
    }

    public override actualizeDisplayedInformation(ce: CalendarEvent = null): void {
        this.clearSelectedRows();
        if (ce == null) {
            this._detailTitleDiv.style.display = "none";
            this._detailTimeDiv.style.display = "none";
            this._detailTextDiv.style.display = "none";
            if (this._actionsMenuButton) {
                this._detailDiv.removeChild(this._actionsMenuButton);
                this._actionsMenuButton = null;
            }
            return;
        }

        this._detailTitleDiv.style.display = "";
        this._detailTimeDiv.style.display = "";
        this._detailTextDiv.style.display = "";
        this.addSelectedRow(ce);
        this._createRowControlElement(ce);
        this._detailTitleDiv.textContent = ce.caption;
        this._detailTimeDiv.textContent = `${DateUtils.dateToString(ce.dateFrom)} - ${DateUtils.dateToString(ce.dateTo)}`;
        this._detailBody.removeChild(this._detailTextDiv);
        this._detailTextDiv = createElement({ classes: "text-fields" });
        //text fields
        if (ce.textFields && ce.textFields.length > 0) {
            for (let i = 0; i < ce.textFields.length; i++) {
                this._detailTextDiv.appendChild(createElement({ value: ce.textFieldsValues[i], attrs: { "data-field": ce.textFields[i] } }));
            }
            this._detailBody.appendChild(this._detailTextDiv);
        }
    }

    public clearSelectedCells(): void {
        while (this.selectedRows.length != 0) {
            this.selectedRows.pop().isSelected = false;
        }
    }

    protected override _createRowControlElement(ce: CalendarEvent): HTMLElement | undefined {
        if (this._actionsMenuButton) {
            this._detailDiv.removeChild(this._actionsMenuButton);
            this._actionsMenuButton = null;
        }

        if (this._rowControlsFactory)
            this._actionsMenuButton = this._rowControlsFactory(() => ce.dataKey);
        else
            return undefined;

        this._detailDiv.appendChild(this._actionsMenuButton);
        return this._actionsMenuButton;
    }

    public override initialize(): void {
        super.initialize();

        this._splitPanel = new SplitPanel();
        this.removeChild(this._mainListDiv);
        this._createDetail();
        this._splitPanel.appendChild(this._mainListDiv);
        this._splitPanel.appendChild(this._detailDiv);
        this.appendChild(this._splitPanel);

        this._rowControlsFactory ? this._rowControlsFactory(() => "") : undefined;
    }

    protected _createInnerGrid(): HTMLElement {
        this._calendarEvents = new Array<CalendarHourEvent>();
        const grid = createElement({ classes: "days-grid" });
        this.actualizeDisplayedInformation();

        if (this.currentDate == undefined)
            this.currentDate = new Date();

        const startDate = new Date(this.currentDate);
        startDate.setHours(0, 0, 0, 0);

        for (let x = 0; x < 24; x++) {
            const lineGrid = createElement({ classes: "day-row" });
            const dayCell = new DayHourCell();
            dayCell.idOfCell = new Date(startDate);
            const hourElement = createElement({ classes: "day-hour" });
            hourElement.textContent = x.toString() + ":00";

            lineGrid.setAttribute("data-hour", x.toString());
            this._dayCells.set(dayCell.hourId, dayCell);
            lineGrid.appendChild(hourElement);
            lineGrid.appendChild(dayCell);

            dayCell.parentElement.addEventListener("click", (_e: MouseEvent) => {
                if (this.selectedCell)
                    this.selectedCell.parentElement.classList.remove("selected");
                this._selectedCell = dayCell;
                this.selectedCell.parentElement.classList.add("selected");
            });

            startDate.setHours(startDate.getHours() + 1);
            grid.appendChild(lineGrid);

            dayCell.eventAdded.add((md, _cvEvent) => {
                for (const event of this._calendarEvents.reverse())
                    this._updateEventPosition(event, md);
            });
        }

        return grid;
    }

    /**
     * Vytvoreni jednoho radku
     */
    protected override _createEventByDS(data: IRowData, uniqueId: string): CalendarHourEvent {
        const dataRow: CalendarHourEvent = CalendarHourEvent.createEventByIRowData(this, uniqueId, data);

        this.dataRows.push(dataRow);

        return dataRow;
    }
    protected override _attachEvent(cv: CalendarHourEvent): void {
        let items = cv.getCalendarEventItems();
        this._calendarEvents.push(cv);
        for (const item of items) {
            const currentAllDayCell = this._allDayCells.get(item.eventId);
            if (currentAllDayCell && item.type == CalendarEventItemType.allday) {
                currentAllDayCell.addEventItem(item);
                currentAllDayCell.eventAdded.add((md, cvEvent) => {
                    //dayCell.removeEmptyItems();

                    if (cv == cvEvent)
                        return;
                    if (item.type != CalendarEventItemType.standalone && item.type != CalendarEventItemType.allday)
                        this._updateEventPosition(cv, md);
                });

                item.addEventListener("calendarEventItemClick", async (e) => {
                    const ce = (e as any).detail as CalendarEvent;
                    this.actualizeDisplayedInformation(ce);
                });
                continue;
            }
            const dayCell = this._dayCells.get(item.eventId);
            if (dayCell == undefined)
                continue;

            dayCell.addEventItem(item);

            item.addEventListener("calendarEventItemClick", async (e) => {
                const ce = (e as any).detail as CalendarEvent;
                this.actualizeDisplayedInformation(ce);
            });

            item.addEventListener("mouseenter", (_e: MouseEvent) => {
                this._mouseOverItem = cv;
            });
            item.addEventListener("mouseleave", (_e: MouseEvent) => {
                this._mouseOverItem = null;
            });
        }

        items = cv.getCalendarEventItems();
    }

    protected override _updateEventPosition(cv: CalendarEvent, cell: CalendarCell): void {
        let items = cv.getCalendarEventItems();

        for (const item of items) {
            if (!DateUtils.compareDates(item.currentDate, cell.idOfCell))
                continue;
            const rRowNum = item.realRowNum;
            item.rowNum = rRowNum;
        }

        items = cv.getCalendarEventItems();
        for (const item of items) {
            if (!DateUtils.compareDates(item.currentDate, cell.idOfCell))
                continue;
            const dayCell = this._dayCells.get(item.eventId);

            if (dayCell && item.rowNum > -1 && cv.rowNum > item.rowNum) {
                while (dayCell.eventItems.indexOf(item) != cv.rowNum) {
                    const nextItem = item.nextElementSibling;
                    if (nextItem && nextItem instanceof CalendarEventItem && nextItem.isEmpty) {
                        dayCell.addEmptyItem(item, "before", 1);
                        nextItem.remove();
                        continue;
                    }
                    if (nextItem && nextItem instanceof CalendarEventItem && nextItem.rowNum < cv.rowNum) {
                        if (dayCell.eventItems.indexOf(nextItem) == nextItem.rowNum)
                            dayCell.addEmptyItem(item, "before", 1);
                        item.remove();
                        nextItem.insertAdjacentElement("afterend", item);

                        const next = item.nextElementSibling;
                        if (next && next instanceof CalendarEventItem && next.isEmpty)
                            next.remove();
                        continue;
                    }
                    dayCell.addEmptyItem(item, "before", cv.rowNum - dayCell.eventItems.indexOf(item));
                    break;
                }
            }
        }

        let hiegherItemCount = 0;

        items = cv.getCalendarEventItems();
        for (const item of items) {
            if (!DateUtils.compareDates(item.currentDate, cell.idOfCell))
                continue;
            const dayCell = this._dayCells.get(item.eventId);
            if (dayCell && dayCell.eventItems != undefined && dayCell.eventItems.length > hiegherItemCount)
                hiegherItemCount = dayCell.eventItems.length;
        }
        items = cv.getCalendarEventItems();
        for (let item of items) {
            if (!DateUtils.compareDates(item.currentDate, cell.idOfCell))
                continue;
            const dayCell = this._dayCells.get(item.eventId);
            if (dayCell && dayCell.eventItems != undefined && dayCell.eventItems.length > 0 && dayCell.eventItems.length < hiegherItemCount) {
                for (let i = item.realRowNum + 1; i < dayCell.eventItems.length; i++) {
                    if (!dayCell.eventItems[i].isEmpty) {
                        item = dayCell.eventItems[i];
                    }
                }
                dayCell.addEmptyItem(item, "after", hiegherItemCount - dayCell.eventItems.length);
            }
        }
    }

    protected _createInnerHeaderGrid(): HTMLElement {
        const grid = createElement({ classes: "header-grid" });

        if (this.currentDate == undefined)
            this.currentDate = new Date();

        const startDate = new Date(this.currentDate);
        startDate.setHours(0, 0, 0, 0);
        this._allDayCells.clear();

        const allDayRow = createElement({ classes: "day-row" });
        const allDayCell = new DayHourCell();
        allDayCell.allDayCell = true;
        allDayCell.idOfCell = new Date(startDate);
        allDayRow.appendChild(allDayCell);
        this._allDayCells.set(allDayCell.hourId, allDayCell);
        grid.appendChild(allDayRow);

        return grid;
    }

    protected override _renderHeader(): void {
        this._mainListDiv.insertAdjacentElement("afterbegin", this._createInnerHeaderGrid());
    }

    private _createDetail(): void {
        this._detailDiv = createElement({ classes: "detail-div" });
        this._detailBody = createElement({ classes: "body" });
        this._detailTitleDiv = createElement({ classes: "title" });
        this._detailTimeDiv = createElement({ classes: "date-time" });
        this._detailTextDiv = createElement({ classes: "text-fields" });

        this._detailBody.appendChild(this._detailTitleDiv);
        this._detailBody.appendChild(this._detailTimeDiv);
        this._detailBody.appendChild(this._detailTextDiv);
        this._detailDiv.appendChild(this._detailBody);

        this.appendChild(this._detailDiv);
    }

    protected override _renderDataContainer(): void {
        this._listDiv = document.createElement("div");

        this._listDiv.classList.add("list-div");
        this._mainListDiv.appendChild(this._listDiv);

        const table = document.createElement("table");

        this._dataTable = document.createElement("tbody");
        this._dataTable.classList.add("table-body");

        table.appendChild(this._dataTable);

        this._listDiv.appendChild(table);

        const innerGrid = this._createInnerGrid();
        this._mainListDiv.insertAdjacentElement("beforeend", innerGrid);

        const firstHourRow = innerGrid.querySelector(`[data-hour='${this._firstHour.toString()}']`) as HTMLElement;
        if (firstHourRow)
            innerGrid.scrollTop = firstHourRow.offsetTop < 20 ? 0 : firstHourRow.offsetTop - 20;
    }
}

registerCustomElement("tsc-DayCalendarGrid", DayCalendarGrid);
